import React, { useState } from "react";
import { useEffect } from "react";
import Table from "../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  getAllTerms,
  createTerm,
  updateTerm,
  toggleTermStatus,
  getTermScopes,
  setTermScopes,
  type Term,
  type CreateTermData,
} from "../../../service/term-management";
import { getAllClasses, type Class } from "../../../service/classes";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectBranches, type Branch } from "../../../core/data/redux/slice/branchesSlice";

// 2026-10-02 RMS-parity rewrite: Term is now global (create "Mid Term"
// once), and WHERE it applies is managed separately via the Scopes modal
// (campus x board, "all classes" or narrowed to specific ones) -- same
// split RMS's own Exam Terms page uses. No more Session field on the
// term itself, and no more re-creating the same term per campus.

type ScopeRow = {
  branchId: string;
  branchName: string;
  systemId: string;
  systemName: string;
  apply: boolean;
  allClasses: boolean;
  classIds: string[];
};

const classSystemId = (c: Class): string => {
  const s = (c as any).system;
  return s && typeof s === "object" ? s._id : s;
};

const TermManagement = () => {
  const routes = all_routes;
  const branches = useSelector(selectBranches) as Branch[];

  const [terms, setTerms] = useState<Term[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
  });

  const [paginationData, setPaginationData] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
  });

  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  const [formData, setFormData] = useState<CreateTermData>({
    title: "",
    type: "",
    rollNumberStrategy: null,
    displayOrder: 0,
    isFinal: false,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const response = await getAllTerms(
        pagination.page,
        pagination.limit,
        searchText || undefined
      );

      if (response.success && Array.isArray(response.data)) {
        setTerms(response.data);
        setPaginationData(response.pagination);
      } else {
        setTerms([]);
      }
    } catch (error) {
      toast.error("Error fetching terms");
      setTerms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, searchText]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = "Title is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const dismissModal = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.setAttribute("data-bs-dismiss", "modal");
      el.click();
      el.removeAttribute("data-bs-dismiss");
    }
  };

  const handleCreateTerm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await createTerm({
        title: formData.title,
        type: formData.type?.trim() || null,
        rollNumberStrategy: formData.rollNumberStrategy || null,
        displayOrder: formData.displayOrder ?? 0,
        isFinal: !!formData.isFinal,
      });
      toast.success("Term created successfully!");

      dismissModal("add_term_modal");
      resetForm();
      await fetchTerms();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Error creating term");
    }
  };

  const handleUpdateTerm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTerm) return;

    if (!validateForm()) return;

    try {
      await updateTerm(editingTerm._id, {
        title: formData.title,
        type: formData.type?.trim() || null,
        rollNumberStrategy: formData.rollNumberStrategy || null,
        displayOrder: formData.displayOrder ?? 0,
        isFinal: !!formData.isFinal,
      });
      toast.success("Term updated successfully!");

      dismissModal("edit_term_modal");

      await fetchTerms();
      setEditingTerm(null);
      resetForm();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Error updating term");
    }
  };

  // Toggle term status (active/inactive). Update local state so term stays in list with updated status.
  const handleToggleStatus = async (term: Term) => {
    if (!term._id) return;

    const newStatus = term.status === "active" ? "inactive" : "active";

    try {
      await toggleTermStatus(term._id, newStatus);
      toast.success(`Term ${newStatus === "active" ? "activated" : "deactivated"} successfully!`);
      setTerms((prev) =>
        prev.map((t) => (t._id === term._id ? { ...t, status: newStatus } : t))
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Error updating term status");
    }
  };

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page });
  };

  const openEditModal = (term: Term) => {
    setEditingTerm(term);
    setFormData({
      title: term.title,
      type: term.type || "",
      rollNumberStrategy: term.rollNumberStrategy || null,
      displayOrder: term.displayOrder ?? 0,
      isFinal: !!term.isFinal,
    });
    setFormErrors({});
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      type: "",
      rollNumberStrategy: null,
      displayOrder: 0,
      isFinal: false,
    });
    setFormErrors({});
  };

  // ── Scopes modal: which (campus, board[, class]) tuples this term applies to ──
  const [scopingTerm, setScopingTerm] = useState<Term | null>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [scopeRows, setScopeRows] = useState<ScopeRow[]>([]);
  const [scopesLoading, setScopesLoading] = useState(false);
  const [savingScopes, setSavingScopes] = useState(false);

  const openScopesModal = async (term: Term) => {
    setScopingTerm(term);
    setScopesLoading(true);
    try {
      const [existingScopes, classesRes] = await Promise.all([
        getTermScopes(term._id),
        classes.length ? Promise.resolve(null) : getAllClasses({ page: 1, limit: 1000, all: "true" }),
      ]);
      if (classesRes) setClasses(classesRes.classes ?? []);

      const rows: ScopeRow[] = [];
      for (const b of branches) {
        for (const s of b.systems || []) {
          const matching = existingScopes.filter((sc) => {
            const scBranch = typeof sc.branch === "object" ? sc.branch._id : sc.branch;
            const scSystem = typeof sc.systemId === "object" ? sc.systemId._id : sc.systemId;
            return scBranch === b._id && scSystem === s._id;
          });
          const applied = matching.length > 0;
          const allClasses = applied && matching.some((sc) => !sc.classId);
          const classIds = matching
            .filter((sc) => sc.classId)
            .map((sc) => (typeof sc.classId === "object" ? sc.classId!._id : (sc.classId as string)));
          rows.push({
            branchId: b._id,
            branchName: b.name,
            systemId: s._id,
            systemName: s.name,
            apply: applied,
            allClasses: applied ? allClasses : true,
            classIds,
          });
        }
      }
      setScopeRows(rows);
    } catch {
      toast.error("Error loading scopes");
      setScopingTerm(null);
    } finally {
      setScopesLoading(false);
    }
  };

  const closeScopesModal = () => {
    dismissModal("scopes_modal");
    setScopingTerm(null);
    setScopeRows([]);
  };

  const toggleScopeApply = (idx: number) => {
    setScopeRows((prev) => prev.map((r, i) => (i === idx ? { ...r, apply: !r.apply } : r)));
  };

  const toggleScopeAllClasses = (idx: number) => {
    setScopeRows((prev) => prev.map((r, i) => (i === idx ? { ...r, allClasses: !r.allClasses } : r)));
  };

  const toggleScopeClass = (idx: number, classId: string) => {
    setScopeRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const classIds = r.classIds.includes(classId)
          ? r.classIds.filter((c) => c !== classId)
          : [...r.classIds, classId];
        return { ...r, classIds };
      })
    );
  };

  const saveScopes = async () => {
    if (!scopingTerm) return;
    setSavingScopes(true);
    try {
      const payload: { branch: string; systemId: string; classId?: string }[] = [];
      for (const row of scopeRows) {
        if (!row.apply) continue;
        if (row.allClasses || row.classIds.length === 0) {
          payload.push({ branch: row.branchId, systemId: row.systemId });
        } else {
          for (const cId of row.classIds) {
            payload.push({ branch: row.branchId, systemId: row.systemId, classId: cId });
          }
        }
      }
      await setTermScopes(scopingTerm._id, payload);
      toast.success("Scopes saved");
      closeScopesModal();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Error saving scopes");
    } finally {
      setSavingScopes(false);
    }
  };

  const columns = [
    {
      title: "Order",
      dataIndex: "displayOrder",
      render: (v: number | undefined) => v ?? 0,
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (type: string | null) =>
        type ? type : <span className="text-muted">—</span>,
    },
    {
      title: "Final?",
      dataIndex: "isFinal",
      render: (v: boolean | undefined) =>
        v ? <i className="ti ti-check text-success fs-5" /> : <span className="text-muted">—</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <>
          {status === "active" ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {status}
            </span>
          ) : (
            <span className="badge badge-soft-danger d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {status || "inactive"}
            </span>
          )}
        </>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text: string, record: Term) => (
        <div className="d-flex align-items-center">
          <Link
            to="#"
            className="btn btn-outline-primary btn-sm me-2"
            data-bs-toggle="modal"
            data-bs-target="#scopes_modal"
            onClick={() => openScopesModal(record)}
          >
            Scopes
          </Link>
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#edit_term_modal"
                  onClick={() => openEditModal(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  onClick={() => handleToggleStatus(record)}
                >
                  <i className={`ti ti-toggle-${record.status === "active" ? "left" : "right"} me-2`} />
                  {record.status === "active" ? "Deactivate" : "Activate"}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Term Management</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Terms
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_term_modal"
                  onClick={() => {
                    setEditingTerm(null);
                    resetForm();
                  }}
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add Term
                </Link>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">All Terms</h4>
            </div>
            <div className="card-body p-0 py-3">
              <p className="text-muted px-3 mb-2">
                A term (First / Mid / Second / Final…) is created once and applies everywhere you
                scope it — use <strong>Scopes</strong> on a row to pick which campuses and board
                systems use it.
              </p>
              <Table
                setSearchText={setSearchText}
                dataSource={terms}
                columns={columns}
                loading={loading}
                pagination={{
                  ...paginationData,
                  onChange: handleChange,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Term Modal */}
      <div className="modal fade" id="add_term_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Term</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreateTerm}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Term Title</label>
                      <input
                        type="text"
                        className={`form-control ${
                          formErrors.title ? "is-invalid" : ""
                        }`}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter term title (e.g., First Term, Second Term)"
                      />
                      {formErrors.title && (
                        <div className="invalid-feedback">
                          {formErrors.title}
                        </div>
                      )}
                      <small className="text-muted">
                        One entry works for every campus — pick where it applies after saving, via Scopes.
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="displayOrder"
                        value={formData.displayOrder ?? 0}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, displayOrder: Number(e.target.value) || 0 }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label d-block">Final Term</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!formData.isFinal}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, isFinal: e.target.checked }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Type</label>
                      <input
                        type="text"
                        className="form-control"
                        name="type"
                        list="term-type-suggestions"
                        value={formData.type || ""}
                        onChange={handleInputChange}
                        placeholder="e.g. Final Examination, Monthly Examination, Mock Examination"
                      />
                      <datalist id="term-type-suggestions">
                        <option value="Final Examination" />
                        <option value="Monthly Examination" />
                        <option value="Mock Examination" />
                      </datalist>
                      <small className="text-muted">
                        Optional — free text, not a fixed list.
                      </small>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Roll Number Style</label>
                      <select
                        className="form-select"
                        name="rollNumberStrategy"
                        value={formData.rollNumberStrategy || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            rollNumberStrategy:
                              (e.target.value as any) || null,
                          }))
                        }
                      >
                        <option value="">Default (Section-wise)</option>
                        <option value="section-wise">Section-wise</option>
                        <option value="whole-class">Whole-class</option>
                      </select>
                      <small className="text-muted">
                        Which roll number format this term uses when generating.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Term
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Term Modal */}
      <div className="modal fade" id="edit_term_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Term</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateTerm}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Term Title</label>
                      <input
                        type="text"
                        className={`form-control ${
                          formErrors.title ? "is-invalid" : ""
                        }`}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter term title (e.g., First Term, Second Term)"
                      />
                      {formErrors.title && (
                        <div className="invalid-feedback">
                          {formErrors.title}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="displayOrder"
                        value={formData.displayOrder ?? 0}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, displayOrder: Number(e.target.value) || 0 }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label d-block">Final Term</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!formData.isFinal}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, isFinal: e.target.checked }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Type</label>
                      <input
                        type="text"
                        className="form-control"
                        name="type"
                        list="term-type-suggestions-edit"
                        value={formData.type || ""}
                        onChange={handleInputChange}
                        placeholder="e.g. Final Examination, Monthly Examination, Mock Examination"
                      />
                      <datalist id="term-type-suggestions-edit">
                        <option value="Final Examination" />
                        <option value="Monthly Examination" />
                        <option value="Mock Examination" />
                      </datalist>
                      <small className="text-muted">
                        Optional — free text, not a fixed list.
                      </small>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Roll Number Style</label>
                      <select
                        className="form-select"
                        name="rollNumberStrategy"
                        value={formData.rollNumberStrategy || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            rollNumberStrategy:
                              (e.target.value as any) || null,
                          }))
                        }
                      >
                        <option value="">Default (Section-wise)</option>
                        <option value="section-wise">Section-wise</option>
                        <option value="whole-class">Whole-class</option>
                      </select>
                      <small className="text-muted">
                        Which roll number format this term uses when generating.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Scopes Modal -- pick which (campus, board[, class]) tuples this term applies to */}
      <div className="modal fade" id="scopes_modal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                Scopes{scopingTerm ? ` — ${scopingTerm.title}` : ""}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                onClick={closeScopesModal}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
              <p className="text-muted">
                Pick where this term applies. Toggle <strong>Apply</strong> on a campus+board row to
                scope it there. Leave <strong>All classes</strong> on for "any class of this board";
                turn it off to narrow to specific classes.
              </p>
              {scopesLoading ? (
                <div className="text-center py-4">Loading…</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {scopeRows.map((row, idx) => (
                    <div key={`${row.branchId}-${row.systemId}`} className="border rounded p-2">
                      <div className="d-flex align-items-center flex-wrap gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={row.apply}
                            onChange={() => toggleScopeApply(idx)}
                          />
                          <label className="form-check-label">
                            {row.branchName} <span className="badge bg-light text-dark">{row.systemName}</span>
                          </label>
                        </div>
                        {row.apply && (
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={row.allClasses}
                              onChange={() => toggleScopeAllClasses(idx)}
                            />
                            <label className="form-check-label">All classes</label>
                          </div>
                        )}
                      </div>
                      {row.apply && !row.allClasses && (
                        <div className="d-flex flex-wrap gap-2 mt-2 ps-4">
                          {classes
                            .filter((c) => classSystemId(c) === row.systemId)
                            .map((c) => (
                              <div className="form-check" key={c._id}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={row.classIds.includes(c._id)}
                                  onChange={() => toggleScopeClass(idx, c._id)}
                                />
                                <label className="form-check-label">{c.name}</label>
                              </div>
                            ))}
                          {classes.filter((c) => classSystemId(c) === row.systemId).length === 0 && (
                            <span className="text-muted">No classes found for this board.</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {scopeRows.length === 0 && (
                    <p className="text-muted">No campuses with a board system configured.</p>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light me-2" onClick={closeScopesModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveScopes}
                disabled={savingScopes || scopesLoading}
              >
                {savingScopes ? "Saving…" : "Save scopes"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermManagement;
